import { Button, Pagination } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useCallback, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CommentsButtons from "src/components/pages/Summary/Review/CommentsButtons";
import QuickVerify from "src/components/pages/Summary/Review/QuickVerify";
import ReviewsTable from "src/components/pages/Summary/ReviewsTable/ReviewsTable";
import FilesModal from "src/components/shared/_Modals/FilesModal/FilesModal";
import { useFilesModal } from "src/components/shared/_Modals/FilesModal/FilesModal.hooks";
import Breadcrumbs from "src/components/shared/Breadcrumbs/Breadcrumbs";
import DateRangeSelect, {
  type DateRange,
} from "src/components/shared/DateRangeSelect/DateRangeSelect";
import { Select } from "src/components/shared/MantineCombobox";
import { PendingFilesProvider } from "src/generator/LogBar/PendingFilesContext";
import { useGetApi, useUploadFileApi } from "src/hooks";
import CompletePopup from "src/pages/Summary/CompletePopup/CompletePopup";
import { mapSummary } from "src/pages/Summary/Summary.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setFiles } from "src/redux/slices/generatorSlice";
import { setComments } from "src/redux/slices/verifySlice";
import { ButtonsWrapper, Flex, PageContainer } from "src/styled";
import { getYachtId, setYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

import CompleteReview from "./CompleteReview/CompleteReview";

interface Props {
  audit?: boolean;
}

const Review = ({ audit }: Props) => {
  const { aid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { upload, isUploading } = useUploadFileApi();
  const [logbook, setLogbook] = useState<string | null>("*");
  const [status, setStatus] = useState<string | null>("*");
  const [dateRange, setDateRange] = useState<DateRange>([null, null]);

  const [page, setPage] = useState(1);

  const [opened, { open, close }] = useDisclosure(false);
  const [popupOpened, { close: closePopup }] = useDisclosure(false);
  const [filesOpened, { open: openFiles, close: closeFiles }] =
    useDisclosure(false);

  const { files } = useSelector(state => state.generatorSlice);
  const { yachtuser, reviewer, auditor } = useSelector(
    state => state.permsSlice,
  );

  const { showRemoved, toggleRemoved, _files, removedNumber } =
    useFilesModal(files);
  // Review/Audit Entries List
  const mapReviews = useCallback(
    (data: any) => {
      setYachtId(data?.data?.review?.audit?.yachtid);
      const result = mapSummary(data);

      dispatch(setComments(result.comments));
      return result;
    },
    [dispatch],
  );

  const {
    data,
    isLoading,
    refetch: refetchReviews,
  } = useGetApi({
    request: (audit ?? !yachtuser) ? "get-reviews-flag" : "get-reviews",
    body: {
      params: {
        _aid: aid,
        sort: searchParams.get("sort"),
        dir: searchParams.get("dir"),
        lbeid: searchParams.get("lbeid"),
        partid: searchParams.get("partid"),
        meid: searchParams.get("meid"),
        ...(logbook && logbook !== "*" && { lbid: logbook }),
        ...(dateRange[0] &&
          dateRange[1] && { startdate: dateRange[0], enddate: dateRange[1] }),
      },
      _status: Number(status),
      _page: page,
    },
    mapper: mapReviews,
  });

  const mapAuditFiles = useCallback(
    (data: any) => {
      dispatch(setFiles(data.list));
    },
    [dispatch],
  );
  const { refetch: fetchFiles } = useGetApi({
    request: "get-audit-files",
    body: { params: { aid } },
    fetchOnInit: false,
    mapper: mapAuditFiles,
  });

  const onOpenFileModal = async () => {
    await fetchFiles();
    openFiles();
  };

  const handleUpload = async (files: File[]) => {
    const body: Record<string, string> = { aid: aid ?? "" };
    await upload("upload-report", files, body, async () => {
      Notification("Success", `The files has been added successfully`, "green");
      await fetchFiles();
    });
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={10}>
        <Breadcrumbs
          customLinks={!yachtuser ? links => links.slice(2) : undefined}
          border={false}>
          {!isLoading && (
            <ButtonsWrapper>
              {data?.pages > 1 && (
                <Pagination
                  total={data?.pages}
                  mr={50}
                  value={page}
                  onChange={setPage}
                />
              )}
              {(audit ? !data?.expired && data?.auditor : false) && (
                <Button variant="outline" onClick={onOpenFileModal}>
                  Files
                </Button>
              )}
              {!data?.expired && (audit ? !yachtuser : true) && (
                <Button
                  variant="outline"
                  onClick={open}
                  color={data?.audit?.status !== 10 ? "green" : undefined}
                  style={{ fontWeight: 200, borderWidth: 2 }}>
                  {data?.audit?.status === 10
                    ? audit
                      ? "View Audit Comments"
                      : "View Review Comments"
                    : audit
                      ? "Complete Audit"
                      : "Complete Review"}
                </Button>
              )}

              {audit && !data?.yachtuser && !data?.expired && data?.auditor && (
                <CommentsButtons
                  status={data.audit.reqstatus}
                  isAudit={audit}
                />
              )}

              {(audit ? data?.auditor : true) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    navigate(
                      `/summary/${
                        audit ? "audits/audit" : "reviews/review"
                      }/${aid}/full-report/${getYachtId()}`,
                    );
                  }}>
                  Report
                </Button>
              )}
              {(auditor || reviewer) && (
                <QuickVerify
                  reviews={data?.reviews ?? []}
                  audit={audit}
                  refetchReviews={refetchReviews}
                />
              )}
            </ButtonsWrapper>
          )}
        </Breadcrumbs>
        <Flex wrap="wrap" justify="flex-end" gap={10}>
          <Select
            data={[
              { label: "All", value: "*" },
              { label: "Action Required", value: "1" },
              { label: "Completed", value: "10" },
            ]}
            value={status}
            onChange={setStatus}
            maw={150}
          />
          <Select
            miw={300}
            data={data?.logbooks ?? [{ label: "All Logbooks", value: "*" }]}
            value={logbook}
            onChange={setLogbook}
          />

          <DateRangeSelect dateRange={dateRange} setDateRange={setDateRange} />
        </Flex>
      </Flex>

      <ReviewsTable
        reviews={data?.reviews ?? []}
        loading={isLoading}
        readonlyComments
        select={auditor || reviewer}
      />
      <CompleteReview
        audit={audit}
        opened={opened}
        onClose={close}
        refetchReviews={refetchReviews}
        canCompleteReview={!(data?.audit?.status === 10)}
      />

      <FilesModal
        opened={filesOpened}
        close={closeFiles}
        handleUpload={handleUpload}
        isUploading={isUploading}
        showRemoved={showRemoved}
        toggleRemoved={toggleRemoved}
        removedNumber={removedNumber}
        _files={_files}
      />
      <CompletePopup
        onClose={closePopup}
        isOpen={popupOpened}
        onYes={open}
        audit={audit}
      />
    </PageContainer>
  );
};

const ReviewWrapper = ({ audit }: Props) => {
  return (
    <>
      <PendingFilesProvider>
        <Review audit={audit} />
      </PendingFilesProvider>
    </>
  );
};

export default ReviewWrapper;
